import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import axios from "axios";
import {
  Smartphone, Tablet, Laptop, MonitorSmartphone, BatteryCharging, PlugZap,
  Layers, Camera, Droplets, RefreshCcw, HardDrive, ArrowLeft, ArrowRight,
  RotateCcw, Phone, MessageSquareText, Mail, MapPin, Check,
} from "lucide-react";
import {
  DEVICE_CATEGORIES, ISSUES,
  getCategory, getTier, getIssue, computeEstimate,
} from "../data/quoteConfig";
import { SITE } from "../data/site";
import { Reveal } from "../components/Reveal";
import { PhoneBgDrift } from "../components/PhoneFloat";
import { LiveIcon } from "../components/LiveIcon";
import { LiminalSection } from "../components/Liminal";
import { HudFrame } from "../components/Liminal";
import { playBlip } from "../utils/sound";
import { useLang } from "../i18n";

// Sticky device stage beside the wizard: starts damaged, resolves to the
// selected device, celebrates with a check burst on the result step.
const STAGE_IMG = {
  iphone: "/phones/iphone.png",
  android: "/phones/samsung.png",
  tablet: "/phones/pixel.png",
  laptop: "/devices/laptop.png",
};

const QuoteStage = ({ categoryId, step }) => {
  const reduce = useReducedMotion();
  const src = STAGE_IMG[categoryId] || "/devices/cracked-iphone.png";
  const done = step === 3;
  return (
    <div
      data-testid="quote-stage"
      className="glass-liquid relative flex aspect-[4/5] items-center justify-center overflow-hidden rounded-[20px]"
    >
      <div className="pointer-events-none absolute -top-16 right-0 h-48 w-48 rounded-full bg-volt/15 blur-3xl" aria-hidden="true" />
      <AnimatePresence mode="wait">
        <motion.img
          key={src}
          src={src}
          alt=""
          draggable="false"
          initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.88, rotate: -5, y: 22 }}
          animate={{ opacity: 1, scale: 1, rotate: 0, y: 0 }}
          exit={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.92, rotate: 4 }}
          transition={{ duration: 0.55, ease: EASE }}
          className={`relative z-10 w-auto ${categoryId === "laptop" ? "h-[46%]" : "h-[64%]"}`}
          style={{ filter: `drop-shadow(0 0 44px ${done ? "rgba(233,180,76,0.5)" : "rgba(46,107,230,0.4)"})` }}
        />
      </AnimatePresence>
      {done && !reduce && (
        <motion.span
          aria-hidden="true"
          className="absolute z-20 h-40 w-40 rounded-full border-2 border-volt"
          initial={{ scale: 0.2, opacity: 0.9 }}
          animate={{ scale: 2.2, opacity: 0 }}
          transition={{ duration: 1.4, ease: "easeOut" }}
        />
      )}
      <div className="absolute bottom-4 left-0 right-0 z-20 flex justify-center">
        <LiveIcon variant={done ? "pulse" : "scan"} color={done ? "#E9B44C" : "#4FB3BF"} testid="quote-stage-icon" />
      </div>
    </div>
  );
};

const EASE = [0.22, 1, 0.36, 1];
const ICONS = {
  smartphone: Smartphone, tablet: Tablet, laptop: Laptop,
  crack: MonitorSmartphone, battery: BatteryCharging, plug: PlugZap, layers: Layers,
  camera: Camera, droplets: Droplets, refresh: RefreshCcw, harddrive: HardDrive,
};

const OptionCard = ({ icon, label, hint, selected, onClick, testid, compact = false }) => {
  const Icon = ICONS[icon] || Smartphone;
  return (
    <motion.button
      type="button"
      data-testid={testid}
      aria-pressed={selected}
      onClick={onClick}
      whileHover={{ y: -4 }}
      whileTap={{ scale: 0.98 }}
      transition={{ duration: 0.35, ease: EASE }}
      className={`group relative flex w-full items-start gap-4 rounded-2xl border p-5 sm:p-6 text-start transition-colors duration-300 ${
        selected
          ? "border-gold bg-gold/10"
          : "border-ink/15 bg-ink/[0.04] hover:border-ink/40 hover:bg-ink/[0.08]"
      }`}
    >
      <span
        className={`flex shrink-0 items-center justify-center rounded-full border transition-colors duration-300 ${
          compact ? "h-10 w-10" : "h-12 w-12"
        } ${selected ? "border-gold bg-gold text-espresso" : "border-ink/25 text-gold-soft"}`}
      >
        <Icon className={compact ? "h-4 w-4" : "h-5 w-5"} />
      </span>
      <span className="flex-1">
        <span className="block font-display text-lg sm:text-xl font-medium tracking-tight">
          {label}
        </span>
        <span className="mt-1 block text-xs sm:text-sm leading-relaxed text-ink/55">{hint}</span>
      </span>
      {selected && (
        <motion.span
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="absolute end-4 top-4 flex h-6 w-6 items-center justify-center rounded-full bg-gold text-espresso"
        >
          <Check className="h-3.5 w-3.5" />
        </motion.span>
      )}
    </motion.button>
  );
};

export default function Quote() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const initialIssue = getIssue(params.get("issue"))?.id || null;
  const initialDevice = getCategory(params.get("device"))?.id || null;

  const [step, setStep] = useState(0);
  const [categoryId, setCategoryId] = useState(initialDevice);
  const [tierId, setTierId] = useState(null);
  const [issueId, setIssueId] = useState(initialIssue);
  const reduce = useReducedMotion();
  const notifiedRef = useRef("");

  const category = getCategory(categoryId);
  const tier = getTier(categoryId, tierId);
  const issue = getIssue(issueId);
  const estimate = useMemo(
    () => computeEstimate(categoryId, tierId, issueId),
    [categoryId, tierId, issueId],
  );

  useEffect(() => {
    if (step !== 3 || !categoryId || !tierId || !issueId) return;
    const key = `${categoryId}:${tierId}:${issueId}`;
    if (notifiedRef.current === key) return;
    notifiedRef.current = key;
    axios
      .post(`${process.env.REACT_APP_BACKEND_URL}/api/quote-notification`, {
        category: categoryId, tier: tierId, issue: issueId,
      })
      .catch(() => {});
  }, [step, categoryId, tierId, issueId]);

  const pickCategory = (id) => { playBlip(560); setCategoryId(id); setTierId(null); setStep(1); };
  const pickTier = (id) => { playBlip(640); setTierId(id); setStep(2); };
  const pickIssue = (id) => { playBlip(720); setIssueId(id); setStep(3); };
  const reset = () => { setCategoryId(null); setTierId(null); setIssueId(null); setStep(0); notifiedRef.current = ""; };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const deviceLabel = tier ? t(`quote.tiers.${categoryId}.${tierId}.label`) : "";
  const issueLabel = issue ? t(`quote.issues.${issueId}.label`) : "";
  const message = encodeURIComponent(
    t("quote.smsBody", { device: deviceLabel, issue: issueLabel }),
  );

  const stageVariants = reduce
    ? { enter: { opacity: 0 }, center: { opacity: 1 }, exit: { opacity: 0 } }
    : { enter: { opacity: 0, x: 48 }, center: { opacity: 1, x: 0 }, exit: { opacity: 0, x: -48 } };

  const STEP_HEADS = [
    { title: t("quote.stepTitle1") },
    { title: t("quote.stepTitle2") },
    { title: t("quote.stepTitle3") },
  ];

  return (
    <LiminalSection env="cyber" testid="quote-page" className="grain min-h-screen pt-28 sm:pt-36 lg:pt-40 pb-20 sm:pb-28">
      <div className="pointer-events-none absolute -top-32 right-0 h-[28rem] w-[28rem] rounded-full bg-volt/10 blur-3xl" aria-hidden="true" />
      <PhoneBgDrift variant="iphone" className="left-[2%] bottom-16 h-[320px] hidden lg:block" />
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
        <Reveal>
          <p className="eyebrow">{t("quote.eyebrow")}</p>
          <h1 className="mt-4 max-w-2xl font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.06]">
            {t("quote.titleA")} <span className="italic text-accent-bright">{t("quote.titleEm")}</span>
          </h1>
          <p className="mt-5 max-w-xl text-base sm:text-lg text-ink/65">
            {t("quote.lede")}
          </p>
        </Reveal>

        <div className="mt-10 grid grid-cols-1 gap-10 sm:mt-14 lg:grid-cols-12">
          <Reveal delay={0.12} className="lg:col-span-7">
          <div
            data-testid="quote-wizard"
            className="glass-liquid grain relative overflow-hidden rounded-[28px] border border-ink/10 p-5 shadow-[0_60px_120px_-50px_rgba(0,0,0,0.9)] sm:p-10"
          >
            <div className="pointer-events-none absolute -bottom-32 -right-24 h-72 w-72 rounded-full bg-accent/20 blur-3xl" aria-hidden="true" />

            {step < 3 && (
              <div className="relative mb-8 sm:mb-10">
                <div className="flex items-center justify-between">
                  <p className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.24em] text-gold-soft">
                    {t("quote.step")} {step + 1} {t("quote.of")} 3
                    <LiveIcon variant="scan" color="#C99B3F" className="!h-7 !w-7 !rounded-lg" />
                  </p>
                  {step > 0 && (
                    <button
                      type="button"
                      data-testid="quote-back-button"
                      onClick={back}
                      className="inline-flex items-center gap-2 rounded-full border border-ink/20 px-4 py-2 text-xs font-semibold text-ink/80 transition-colors hover:border-ink/50 hover:text-ink"
                    >
                      <ArrowLeft className="h-3.5 w-3.5 rtl:-scale-x-100" /> {t("quote.back")}
                    </button>
                  )}
                </div>
                <div className="mt-4 h-[3px] w-full overflow-hidden rounded-full bg-ink/10">
                  <motion.div
                    data-testid="quote-progress"
                    className="h-full rounded-full bg-gold"
                    initial={false}
                    animate={{ width: `${((step + 1) / 3) * 100}%` }}
                    transition={{ duration: 0.7, ease: EASE }}
                  />
                </div>
                <h2 className="mt-5 font-display text-2xl sm:text-3xl font-medium tracking-tight">
                  {STEP_HEADS[step].title}
                </h2>
              </div>
            )}

            <AnimatePresence mode="wait">
              {step === 0 && (
                <motion.div
                  key="s0"
                  variants={stageVariants}
                  initial="enter" animate="center" exit="exit"
                  transition={{ duration: 0.45, ease: EASE }}
                  className="relative grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4"
                >
                  {DEVICE_CATEGORIES.map((c) => (
                    <OptionCard
                      key={c.id}
                      testid={`quote-option-${c.id}`}
                      icon={c.icon}
                      label={t(`quote.categories.${c.id}.label`)}
                      hint={t(`quote.categories.${c.id}.blurb`)}
                      selected={categoryId === c.id}
                      onClick={() => pickCategory(c.id)}
                    />
                  ))}
                </motion.div>
              )}

              {step === 1 && category && (
                <motion.div
                  key="s1"
                  variants={stageVariants}
                  initial="enter" animate="center" exit="exit"
                  transition={{ duration: 0.45, ease: EASE }}
                  className="relative space-y-3 sm:space-y-4"
                >
                  {category.tiers.map((tr) => (
                    <OptionCard
                      key={tr.id}
                      testid={`quote-tier-${tr.id}`}
                      icon={category.icon}
                      label={t(`quote.tiers.${category.id}.${tr.id}.label`)}
                      hint={t(`quote.tiers.${category.id}.${tr.id}.hint`)}
                      selected={tierId === tr.id}
                      onClick={() => pickTier(tr.id)}
                    />
                  ))}
                </motion.div>
              )}

              {step === 2 && (
                <motion.div
                  key="s2"
                  variants={stageVariants}
                  initial="enter" animate="center" exit="exit"
                  transition={{ duration: 0.45, ease: EASE }}
                  className="relative grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4"
                >
                  {ISSUES.map((i) => (
                    <OptionCard
                      key={i.id}
                      testid={`quote-issue-${i.id}`}
                      icon={i.icon}
                      label={t(`quote.issues.${i.id}.label`)}
                      hint={t(`quote.issues.${i.id}.hint`)}
                      selected={issueId === i.id}
                      onClick={() => pickIssue(i.id)}
                      compact
                    />
                  ))}
                </motion.div>
              )}

              {step === 3 && category && tier && issue && (
                <motion.div
                  key="s3"
                  variants={stageVariants}
                  initial="enter" animate="center" exit="exit"
                  transition={{ duration: 0.5, ease: EASE }}
                  className="relative"
                  data-testid="quote-result"
                >
                  <div className="flex items-center gap-4">
                    <motion.span
                      initial={reduce ? false : { scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 300, damping: 16, delay: 0.1 }}
                      className="flex h-12 w-12 items-center justify-center rounded-full bg-gold text-espresso"
                    >
                      <Check className="h-6 w-6" />
                    </motion.span>
                    <p className="font-display text-2xl sm:text-3xl font-medium tracking-tight">
                      {t("quote.resultTitle")}
                    </p>
                  </div>

                  <div className="mt-6 flex flex-wrap gap-2">
                    <span data-testid="quote-summary-device" className="rounded-full border border-ink/20 bg-ink/5 px-4 py-2 text-sm text-ink/85">
                      {deviceLabel}
                    </span>
                    <span data-testid="quote-summary-issue" className="rounded-full border border-ink/20 bg-ink/5 px-4 py-2 text-sm text-ink/85">
                      {issueLabel}
                    </span>
                  </div>

                  <div
                    data-testid="quote-range"
                    className="mt-7 rounded-2xl border border-gold/30 bg-gold/[0.07] px-6 py-5"
                  >
                    <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-gold-soft">
                      {t("quote.preliminary")} · {t("quote.planning")}
                    </p>
                    <p className="mt-2 font-display text-3xl sm:text-4xl font-medium tracking-tight text-gold">
                      CA ${estimate.low}–${estimate.high}
                    </p>
                    <p className="mt-2 text-xs leading-relaxed text-ink/50">
                      {t("quote.disclaimer")}
                    </p>
                  </div>

                  <p className="mt-6 max-w-xl text-sm sm:text-base leading-relaxed text-ink/65">
                    {t("quote.callLead")}
                  </p>

                  <a
                    href={SITE.phoneHref}
                    data-testid="quote-call-button"
                    className="group mt-7 flex items-center justify-between rounded-2xl bg-accent px-6 py-5 font-semibold text-[#EDF1F7] transition-colors hover:bg-accent-deep"
                  >
                    <span className="flex items-center gap-3 text-lg sm:text-xl">
                      <Phone className="h-6 w-6" /> {t("quote.call")} {SITE.phoneDisplay}
                    </span>
                    <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1 rtl:-scale-x-100" />
                  </a>

                  <div className="mt-6 grid grid-cols-1 gap-8 border-t border-ink/12 pt-8 sm:grid-cols-2">
                    <div>
                      <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-ink/50">
                        {t("quote.readyTitle")}
                      </p>
                      <ul className="mt-4 space-y-2.5 text-sm leading-relaxed text-ink/70">
                        {t("quote.ready").map((f) => (
                          <li key={f} className="flex gap-3">
                            <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-gold" />
                            {f}
                          </li>
                        ))}
                      </ul>
                      <p className="mt-5 text-xs leading-relaxed text-ink/45">
                        {t("quote.noPrice")}
                      </p>
                    </div>
                    <div className="flex flex-col gap-3">
                      <a
                        href={`${SITE.smsHref}?&body=${message}`}
                        data-testid="quote-text-button"
                        className="group flex items-center justify-between rounded-2xl border border-ink/25 px-6 py-4 font-semibold transition-colors hover:bg-ink/10"
                      >
                        <span className="flex items-center gap-3"><MessageSquareText className="h-5 w-5" /> {t("quote.text")}</span>
                        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1 rtl:-scale-x-100" />
                      </a>
                      <a
                        href={`${SITE.emailHref}?subject=${encodeURIComponent(t("quote.emailSubject"))}&body=${message}`}
                        data-testid="quote-email-button"
                        className="group flex items-center justify-between rounded-2xl border border-ink/25 px-6 py-4 font-semibold transition-colors hover:bg-ink/10"
                      >
                        <span className="flex items-center gap-3"><Mail className="h-5 w-5" /> {t("quote.email")}</span>
                        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1 rtl:-scale-x-100" />
                      </a>
                      <div className="mt-2 flex flex-wrap items-center gap-4">
                        <button
                          type="button"
                          data-testid="quote-reset-button"
                          onClick={reset}
                          className="inline-flex items-center gap-2 rounded-full border border-ink/20 px-5 py-2.5 text-xs font-semibold text-ink/75 transition-colors hover:border-ink/50 hover:text-ink"
                        >
                          <RotateCcw className="h-3.5 w-3.5" /> {t("quote.startOver")}
                        </button>
                        <Link
                          to="/visit"
                          data-testid="quote-visit-link"
                          className="inline-flex items-center gap-2 text-xs font-semibold text-gold-soft transition-colors hover:text-gold"
                        >
                          <MapPin className="h-3.5 w-3.5" /> {t("quote.visitShop")} — {SITE.locationCue}
                        </Link>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
          <div className="hidden lg:col-span-5 lg:block">
            <div className="sticky top-28">
              <HudFrame label="Estimate Core" coords="CAD · PRE-TAX" className="p-1.5">
                <QuoteStage categoryId={categoryId} step={step} />
              </HudFrame>
            </div>
          </div>
        </div>

        <Reveal delay={0.2} className="mt-8">
          <p className="text-center font-mono text-[11px] uppercase tracking-[0.22em] text-ink/45">
            {SITE.address} · {SITE.locationCue} · {SITE.phoneIntl}
          </p>
        </Reveal>
      </div>
    </LiminalSection>
  );
}
